/**
 * Everything the screens read and write, behind two interfaces (ADR 0004):
 *
 *  - `Backend`: the cross-tenant part of a session — who am I, which clubs,
 *    the platform area —
 *    and `forTenant(id)`, which hands out the tenant-scoped source.
 *  - `DataSource`: one tenant's business data. Every method maps to a route
 *    under /api/t/{tenantId}/…; a tenant without membership answers 404.
 *
 * Two implementations, never mixed (backendFactory.ts): the API adapter
 * (apiSource.ts, 1:1 to docs/api.md) for a real session, and the in-browser
 * demo store (demoSource.ts) for the marketing demo. A new feature therefore
 * needs both; the interface makes the compiler insist. Screens never import a
 * source directly; they go through hooks.ts.
 *
 * Every write returns the id of the change event it produced, so the undo
 * toast can name what it compensates (spec section 10: "Rückgängig" is a
 * compensating write, logged as its own event 'undo').
 */
import type { AircraftView } from '@fsv/core';
import type {
  AddImagesResult,
  Aircraft,
  AircraftNotice,
  AircraftPatch,
  AircraftType,
  AircraftTypePatch,
  ChangeEvent,
  ClubContact,
  ClubPatch,
  CreateDamageInput,
  CreateDamageResult,
  Damage,
  DamageComment,
  DamageDetail,
  DamageImage,
  DrawingFile,
  DrawingUploadResult,
  EventFilter,
  ExportLink,
  FleetCounts,
  Billing,
  ExportResult,
  NewNoticeInput,
  NoticePatch,
  FleetAircraft,
  FleetAircraftType,
  ForceOptions,
  FleetEntry,
  ImageUploadFailure,
  ImportSummary,
  InboxEntry,
  Me,
  Member,
  MyEmails,
  MemberImportRow,
  MemberPatch,
  NewAircraftInput,
  NewAircraftTypeInput,
  NewImage,
  NewMemberInput,
  NewTenantInput,
  Contract,
  ContractPatch,
  NewContractInput,
  ReviewInput,
  SearchQuery,
  Settings,
  Tenant,
  TenantAdminRef,
  TenantDetail,
  TenantPatch,
  TrashItem,
  UpdateDamageInput,
  UploadOptions,
  WriteOptions,
  WriteResult,
} from './types';
import type { AnnouncementRef } from '../news/news';

/**
 * 0 = the server was not reached (offline), 401 = session gone, 503 = photo
 * storage or Key Vault unavailable; the rest as in docs/api.md.
 */
export type DataErrorStatus = 0 | 400 | 401 | 403 | 404 | 409 | 503;

export class DataError extends Error {
  constructor(
    public readonly status: DataErrorStatus,
    message: string,
    /**
     * Machine-readable reason: the server's `code` (`forbidden_field`, `exif`, …)
     * or, set by the adapter, `stale` for a write refused because the record
     * changed since it was read (the screen then reloads). `pins_drift` is a
     * question for the person (send again with `force`), never `stale`.
     */
    public readonly code?: string,
    /** Extra facts for the screen; today only `uploadId` on a `pins_drift` from uploadDrawing. */
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'DataError';
  }
}

/** Every photo of a batch was refused; `failures` says why, per photo. */
export class PhotoUploadError extends DataError {
  constructor(
    message: string,
    public readonly failures: ImageUploadFailure[],
  ) {
    super(400, message, 'upload_failed');
    this.name = 'PhotoUploadError';
  }
}

export interface DataSource {
  readonly kind: 'demo' | 'api';
  /** The tenant every call is scoped to (/api/t/{tenantId}/…). */
  readonly tenantId: string;

  // --- reads ---------------------------------------------------------------
  getSettings(): Promise<Settings>;
  listAircraftTypes(): Promise<AircraftType[]>;
  /** Active aircraft with counts; admins get inactive ones from listAircraft(). */
  listFleet(): Promise<FleetEntry[]>;
  listAircraft(): Promise<Aircraft[]>;
  /** All non-deleted damages of one aircraft, every view and status; pins carry no photos except a thumbnail. */
  listDamages(registration: string): Promise<Damage[]>;
  getDamage(id: string): Promise<DamageDetail>;
  search(query: SearchQuery): Promise<Damage[]>;
  /** Reports in status 'gemeldet', oldest first, with what is nearby. */
  inbox(): Promise<InboxEntry[]>;
  myReports(): Promise<Damage[]>;
  listMembers(): Promise<Member[]>;
  listEvents(filter: EventFilter): Promise<ChangeEvent[]>;
  listTrash(): Promise<TrashItem[]>;

  // --- damages -------------------------------------------------------------
  /** Photos in `input.images` are uploaded after the damage exists; refused ones are listed, not thrown. */
  createDamage(input: CreateDamageInput, upload?: UploadOptions): Promise<CreateDamageResult>;
  updateDamage(id: string, patch: UpdateDamageInput, options?: WriteOptions): Promise<WriteResult<Damage>>;
  reviewDamage(id: string, input: ReviewInput): Promise<WriteResult<Damage>>;
  /** Compensates a review event: restores status and review fields from its before-snapshot. */
  revertReview(id: string, eventId: number): Promise<WriteResult<Damage>>;
  deleteDamage(id: string, options?: WriteOptions): Promise<WriteResult<Damage>>;
  restoreDamage(id: string, options?: WriteOptions): Promise<WriteResult<Damage>>;

  // --- notices of the technicians (0022) -------------------------------------
  /**
   * Active notices of the club, most urgent first. Editors and admins get
   * `readers` (who confirmed „gelesen“). An older API without the route answers
   * with an empty list in the adapter, never an error: notices are extra.
   */
  listNotices(): Promise<AircraftNotice[]>;
  /** Editors and admins: every notice of one aircraft, resolved and expired ones included, newest first. */
  listNoticeHistory(aircraftId: string): Promise<AircraftNotice[]>;
  createNotice(input: NewNoticeInput): Promise<WriteResult<AircraftNotice>>;
  /** `expectedUpdatedAt`: the notice's updatedAt when the form opened; a newer one is DataError `stale`. */
  updateNotice(id: string, patch: NoticePatch, options?: WriteOptions): Promise<WriteResult<AircraftNotice>>;
  resolveNotice(id: string): Promise<WriteResult<AircraftNotice>>;
  /** „Gelesen“ by the person signed in; answers the notice as they now see it. */
  acknowledgeNotice(id: string): Promise<AircraftNotice>;

  // --- images and comments -------------------------------------------------
  /**
   * `mergedReport`: photos from a duplicate report ("Ist derselbe Schaden"), logged as merged_report.
   * Uploads one photo after the other; a refused photo is listed in `failures` and the rest continue.
   * Throws PhotoUploadError only when not a single photo made it.
   */
  addImages(
    damageId: string,
    images: NewImage[],
    options?: WriteOptions & UploadOptions & { mergedReport?: { note: string | null } | undefined },
  ): Promise<AddImagesResult>;
  removeImage(imageId: string, options?: WriteOptions): Promise<WriteResult<DamageImage>>;
  restoreImage(imageId: string, options?: WriteOptions): Promise<WriteResult<DamageImage>>;
  addComment(damageId: string, body: string): Promise<WriteResult<DamageComment>>;
  deleteComment(commentId: string, options?: WriteOptions): Promise<WriteResult<DamageComment>>;
  restoreComment(commentId: string, options?: WriteOptions): Promise<WriteResult<DamageComment>>;

  // --- administration ------------------------------------------------------
  addMember(input: NewMemberInput): Promise<WriteResult<Member>>;
  updateMember(id: string, patch: MemberPatch, options?: WriteOptions): Promise<WriteResult<Member>>;
  /**
   * `deactivate`: addresses the file lists with a status that does not count as
   * membership (a Vereinsflieger export's "passiv", say); members among them are
   * deactivated, nobody is added for them.
   */
  importMembers(
    rows: MemberImportRow[],
    deactivateMissing: boolean,
    deactivate?: string[],
    /** New active members get an invitation, sent by the server in steps (about INVITATIONS_PER_HOUR an hour). */
    invite?: boolean,
  ): Promise<WriteResult<ImportSummary>>;
  /** „Einladung senden“ (again): marks the invitation as pending; the server sends it within minutes. 409 for a former member. */
  inviteMember(id: string): Promise<Member>;
  endMemberSessions(id: string): Promise<void>;
  setAircraftActive(id: string, active: boolean, options?: WriteOptions): Promise<WriteResult<Aircraft>>;
  updateSettings(patch: Partial<Settings>, options?: WriteOptions): Promise<WriteResult<Settings>>;
  /**
   * POST manage/members/{id}/anonymize: name and address become `geloescht-…`,
   * the membership ends, sessions end. 409 when already anonymised, still
   * active in another club, or the last admin. No undo.
   */
  anonymizeMember(id: string): Promise<WriteResult<Member>>;
  /**
   * PUT manage/members/{id}/email: the address the person signs in with. The
   * old one stops working at once (sessions, code, sign-in link end). 409 for
   * an anonymised person, one of another club, a platform manager, the same
   * address or one another person has (`de.emailChange`). No undo.
   */
  changeMemberEmail(id: string, email: string): Promise<WriteResult<Member>>;
  /**
   * POST manage/members/{id}/emails: a further sign-in address (0023), valid
   * at once. 409 (`de.additionalEmail`): anonymised, in another club (also
   * formerly), platform manager (person or address), already theirs, another
   * person's, or past MAX_ADDITIONAL_EMAILS. No undo.
   */
  addMemberEmail(id: string, email: string): Promise<WriteResult<Member>>;
  /**
   * DELETE manage/members/{id}/emails/{emailId}: stops working at once —
   * sessions opened with it end. 409 for another club's person or a platform
   * manager. No undo.
   */
  removeMemberEmail(id: string, emailId: string): Promise<WriteResult<Member>>;

  // --- fleet (docs/fleet-admin-ui.md) ----------------------------------------
  /** GET manage/aircraft — inactive and deleted ones too, with the damage count. */
  listFleetAdmin(): Promise<FleetAircraft[]>;
  /** POST manage/aircraft. 409: registration taken (the text says whether by a deleted one). */
  createAircraft(input: NewAircraftInput): Promise<WriteResult<FleetAircraft>>;
  /** PATCH manage/aircraft/{id}. 409 'pins_drift' for a type change without force. */
  updateAircraft(id: string, patch: AircraftPatch, options?: ForceOptions): Promise<WriteResult<FleetAircraft>>;
  /** DELETE manage/aircraft/{id}. 409 with damages ("stattdessen außer Dienst stellen"). */
  deleteAircraft(id: string, options?: WriteOptions): Promise<WriteResult<FleetAircraft>>;
  /** POST manage/aircraft/{id}/restore. */
  restoreAircraft(id: string, options?: WriteOptions): Promise<WriteResult<FleetAircraft>>;
  /** GET manage/aircraft-types — own first, then the catalogue. */
  listFleetTypes(): Promise<FleetAircraftType[]>;
  /** POST manage/aircraft-types. 409: code taken (catalogue or own). */
  createAircraftType(input: NewAircraftTypeInput): Promise<WriteResult<FleetAircraftType>>;
  /** PATCH manage/aircraft-types/{id}. Catalogue: 403. */
  updateAircraftType(id: string, patch: AircraftTypePatch, options?: WriteOptions): Promise<WriteResult<FleetAircraftType>>;
  /**
   * upload-url → PUT → register, in one call. A 409 'pins_drift' carries
   * `uploadId` in DataError.details, so confirmDrawing can follow up without
   * uploading again.
   */
  uploadDrawing(typeId: string, view: AircraftView, file: DrawingFile, options?: ForceOptions & UploadOptions): Promise<DrawingUploadResult>;
  /** PUT …/drawings/{view} with an uploaded uuid: force after the question, or the undo of a replacement. */
  confirmDrawing(typeId: string, view: AircraftView, uploadId: string, options?: ForceOptions): Promise<DrawingUploadResult>;

  // --- club export (admin) ---------------------------------------------------------
  /** POST manage/export: builds the archive, logs 'exported', answers with a 1-hour link. */
  createExport(): Promise<WriteResult<ExportResult>>;
  /** GET manage/export: the files of the last RETENTION.exportDays days, newest first, each with a fresh link. */
  listExports(): Promise<ExportLink[]>;

  // --- the club itself (admin) -------------------------------------------------
  getClub(): Promise<Tenant>;
  /** Every member: the club's name and contact data, for the privacy page (the club is the controller). */
  getClubContact(): Promise<ClubContact>;
  updateClub(patch: ClubPatch, options?: WriteOptions): Promise<WriteResult<Tenant>>;
  /** PUT manage/logo (0019): a PNG the device prepared (`prepareLogo`), checked by `inspectLogo`. Members see it through `me`. */
  setClubLogo(pngBase64: string): Promise<WriteResult<{ logoUrl: string | null }>>;
  /** DELETE manage/logo: back to the club's code. */
  removeClubLogo(): Promise<WriteResult<{ logoUrl: null }>>;
}

/** /api/platform/* — platform managers only. Master data, never business data. */
export interface PlatformSource {
  listTenants(): Promise<Tenant[]>;
  getTenant(tenantId: string): Promise<TenantDetail>;
  createTenant(input: NewTenantInput): Promise<WriteResult<Tenant>>;
  updateTenant(tenantId: string, patch: TenantPatch): Promise<WriteResult<Tenant>>;
  addTenantAdmin(tenantId: string, admin: { name: string; email: string }): Promise<WriteResult<TenantAdminRef>>;
  /** POST /platform/tenants/{id}/export — also for an inactive club (offboarding); the one route into business data. */
  createTenantExport(tenantId: string): Promise<WriteResult<ExportResult>>;
  listTenantExports(tenantId: string): Promise<ExportLink[]>;
  /** Contracts (0024): every contract, sign-ups included, newest first. */
  listContracts(): Promise<Contract[]>;
  getContract(contractId: string): Promise<Contract>;
  /** A contract for an EXISTING club, concluded outside the sign-up form (FSV 1910: founder rank 1). */
  createContract(input: NewContractInput): Promise<WriteResult<Contract>>;
  updateContract(contractId: string, patch: ContractPatch): Promise<WriteResult<Contract>>;
  terminateContract(contractId: string, endsOn: string): Promise<WriteResult<Contract>>;
  /** A further area of the club joins the contract (0026): one contract, one invoice, separate tenants. */
  addContractDepartment(contractId: string, tenantId: string): Promise<WriteResult<Contract>>;
  removeContractDepartment(contractId: string, tenantId: string): Promise<WriteResult<Contract>>;
  /** GET /platform/fleet-counts?month=YYYY-MM (0025): every club's active aircraft by class on the 1st. */
  fleetCounts(month: string): Promise<FleetCounts>;
  /** GET /platform/billing?month=YYYY-MM: per club and contract what the month bills, with totals. */
  billing(month: string): Promise<Billing>;
  /** GET /platform/billing.csv?month=YYYY-MM: the same as CSV text (BOM, semicolons) for the accounting. */
  billingCsv(month: string): Promise<string>;
}

export interface Backend {
  readonly kind: 'demo' | 'api';
  /** GET /api/me: the person, the platform-manager flag and the active memberships. */
  me(): Promise<Me>;
  /** The source for one tenant. Cheap; callers may memoise it per tenant id. */
  forTenant(tenantId: string): DataSource;
  readonly platform: PlatformSource;
  /**
   * GET /api/me/announcements: the "Neuigkeiten" this person has not seen,
   * oldest first (ids and dates; the words are in the bundle, news/news.ts).
   * The demo answers from the device: the newest few, until dismissed there.
   */
  announcements(): Promise<AnnouncementRef[]>;
  /** POST /api/me/announcements/ack: exactly what was shown. Unknown ids are ignored by the server. */
  acknowledgeAnnouncements(ids: readonly string[]): Promise<void>;
  /** GET /api/me/welcome (0015): whether the welcome with the tour is still due for this person. Demo: never. */
  welcomeDue(): Promise<boolean>;
  /** POST /api/me/welcome: started or skipped, not again on any device. */
  finishWelcome(): Promise<void>;
  /**
   * PUT /api/me/avatar: the person's own picture, a square JPEG the device
   * prepared (256 px, no metadata), as base64. Answers the updated `me`.
   */
  setAvatar(jpegBase64: string): Promise<Me>;
  /** DELETE /api/me/avatar. */
  removeAvatar(): Promise<Me>;
  /** GET /api/me/emails (0023): the person's own sign-in addresses. */
  myEmails(): Promise<MyEmails>;
  /**
   * POST /api/me/emails: a code goes to that address; `pending` names it.
   * 409 `own` / `limit` (`de.additionalEmail`); nothing else is said about the
   * address. The demo sends no mail and takes DEMO_EMAIL_CODE.
   */
  addMyEmail(email: string): Promise<MyEmails>;
  /** POST /api/me/emails/confirm: the code from that mail; from now on the address signs in too. */
  confirmMyEmail(code: string): Promise<MyEmails>;
  /** DELETE /api/me/emails/{id}: stops working at once, also on devices signed in with it. */
  removeMyEmail(id: string): Promise<MyEmails>;
  /** Demo only: throw away all local changes. */
  reset?(): Promise<void>;
  /** Called when the session ends: drops what the backend holds for it (API: the drawing object URLs). */
  dispose?(): void;
}
